import { HttpClient, HttpParams, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { AppConfig } from '../configuration/app.config';
import * as signalR from '@microsoft/signalr';
import { LivesSessionHelper } from '../helper/livesSessionHelper';

@Injectable({ providedIn: 'root' })
export class AppChatService {

  public readonly userId = this._livesSession.AspNetUserId;
  public connection: signalR.HubConnection = new signalR.HubConnectionBuilder()
    .withUrl(`${AppConfig.settings.apiServerPE.chatHubUrl}?userId=${this.userId}&roomId=''`)
    .configureLogging(signalR.LogLevel.Information)
    .build();

  public messages$ = new BehaviorSubject<any>([]);
  public connectedUsers$ = new BehaviorSubject<string[]>([]);
  public messages: any[] = [];
  public users: string[] = [];

  constructor(
    protected http: HttpClient,
    private _livesSession: LivesSessionHelper
  ) {
    this.startConnection();
  }

  // Start connection
  public async startConnection() {
    try {
      await this.stopConnection();
      await this.connection.start();
    } catch (error) {
      console.error(error);
    }
  }

  // Stop connection
  public async stopConnection() {
    try {
      await this.connection.stop();
    } catch (error) {
      console.error(error);
    }
  }

  public async messagesInvokes() {
    this.connection.on("ReceiveMessage", (response) => {
      const newMessage = this.formatMessages([response])[0];
      this.messages = [...this.messages, newMessage];
      this.messages$.next(this.messages);
    });

    this.connection.on("UpdateMessageStatus", (response) => {
      const updatedMessage = this.formatMessages([response])[0];
      this.messages = this.messages.map(msg => msg.messageId === updatedMessage.messageId ? updatedMessage : msg);
      this.messages$.next(this.messages);
    });
  }

  private async waitForConnection(): Promise<void> {
    const MAX_RETRIES = 15;
    let retries = 0;

    while (this.connection.state !== signalR.HubConnectionState.Connected) {
      if (retries > MAX_RETRIES) {
        throw new Error("Max retries reached, connection could not be established");
      }
      await new Promise(resolve => setTimeout(resolve, 100)); // Wait for 1 second before retrying
      retries++;
    }
  }

  // Create Room
  public async createChatRoom(user: string, users: [{ userId: string; userType: string }], company: string) {
    await this.stopConnection(); // Close the existing connection
    await this.startConnection(); // Start a new connection
    this.messagesInvokes();
    return this.connection.invoke("CreateChatRoom", user, users, company);
  }

  // Join Room
  public async joinChatRoom(user: string, room: string) {
    await this.stopConnection(); // Close the existing connection
    await this.startConnection(); // Start a new connection
    this.messagesInvokes();
    return this.connection.invoke("JoinChatRoom", user, room);
  }

  // Send Messages
  public async sendMessage(room: string, user: string, message: string) {
    return this.connection.invoke("SendMessage", room, user, message);
  }

  // Get Messages
  public async getMessagesFromRoom(room: string) {
    return this.connection.invoke("GetMessagesForRoom", room);
  }

  // Update Messages
  public async UpdateMessagesFromRoom(room: string, user: string, messageId: string) {
    this.connection.invoke("MarkMessageAsReadByUser", room, user, messageId);
  }

  // List Rooms
  public async getChatRoomsForUser(user: string) {
    await this.waitForConnection();
    const rooms = await this.connection.invoke("GetChatRoomsForUser", user);
    const newChats = [];

    for (const room of rooms) {
      const chatMembersInfo = await this.connection.invoke("GetChatMembersInfo", room.roomId);
      newChats.push({
        roomId: room.roomId,
        chatMembersInfo: chatMembersInfo,
        lastMessage: room.lastMessageText,
        lastMessageTime: room.lastMessageTime,
        lastMessageIsNew: room.lastMessageIsNew,
        company: room.company,
      });
    }

    return newChats;
  }

  public getNewChats(userId: any, companyId: any): Observable<any> {
    const url = `${AppConfig.settings.apiServerPE.apiUrl}chathub/chatableusers?userId=${userId}&companyId=${companyId}`;

    // Crear los headers con la clave API
    const headers = new HttpHeaders({
      'X-API-KEY': AppConfig.settings.apiServerPE.apiKey
    });

    // Realizar la petición GET y devolver el resultado como un Observable
    return this.http.get<any>(url, { headers: headers });
  }

  public formatMessages(messages: any) {
    return messages
      .map(msg => ({
        messageId: msg.message,
        id: msg.id || `${msg.sender}-${msg.sentAt.substring(0, 25)}`, // establecer id único
        sender: msg.sender || msg.userId,
        user: msg.user,
        messageText: msg.messageText || msg.msg,
        sentAt: msg.sentAt || new Date().toISOString(),
        roomId: msg.room,
        isRead: msg.isRead || false,
      }))
      .sort((a, b) => new Date(a.sentAt).getTime() - new Date(b.sentAt).getTime());
  }

}
